import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Col, Row, Table, Spinner, Badge, Container, Button } from 'react-bootstrap';
import axios from 'axios';
import { authHeader } from '../../../services/auth-header';
import { BASE_URL } from '../../../services/Api';
import { FaArrowLeft, FaCopy } from 'react-icons/fa';

function PIView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        enquiryno: '',
        enquirydate: '',
        status: '',
        supplier: null,
        contactperson: '',
        contactno: '',
        email: '',
        supplierMultipleAddress: null,
        customermaterialcode: '',
        currency: '',
        purchaseEnquiryItems: []
    });
    const [enquiryData, setEnquiryData] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(`${BASE_URL}purchaseEnquiry/findById/${id}`, {
                    headers: authHeader()
                });

                setEnquiryData(res.data);
                const enquiry = res.data;

                setData({
                    enquiryno: enquiry.enquiryno,
                    enquirydate: enquiry.enquirydate,
                    status: enquiry.status,
                    supplier: {
                        value: enquiry.supplier?.id,
                        label: enquiry.supplier?.companyname,
                        gstno: enquiry.supplier?.gstno,
                    },
                    contactperson: enquiry.contactperson,
                    contactno: enquiry.contactno,
                    email: enquiry.email,
                    supplierMultipleAddress: enquiry.supplierMultipleAddress ? {
                        value: enquiry.supplierMultipleAddress.id,
                        label: enquiry.supplierMultipleAddress.supplieraddress,
                        city: enquiry.supplierMultipleAddress.city,
                        state: enquiry.supplierMultipleAddress.state,
                        pincode: enquiry.supplierMultipleAddress.pincode,
                        country: enquiry.supplierMultipleAddress.country,
                    } : null,
                    customermaterialcode: enquiry.customermaterialcode,
                    currency: enquiry.purchaseEnquiryItems?.length > 0 ? enquiry.purchaseEnquiryItems[0].currency : '',
                    purchaseEnquiryItems: enquiry.purchaseEnquiryItems?.map(item => ({
                        ...item,
                        description: item.product?.description || item.label,
                        partno: item.partno || item.product?.partno,
                        serialnumber: item.serialnumber || item.product?.serialnumber,
                        modelnumber: item.modelnumber || item.product?.modelnumber,
                        tagnumber: item.tagnumber || item.product?.tagnumber,
                        moc: item.moc || item.product?.moc,
                        hsnsacno: item.hsnsacno || item.product?.hsnsacno,
                        itemcode: item.itemcode || item.product?.itemcode,
                        uom: item.uom || item.product?.unitofmeasurement,
                        quantity: item.quantity,
                        currency: item.currency,
                        productremarks: item.productremarks,
                    })) || []
                });
            } catch (err) {
                setError('Failed to fetch enquiry details. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const getAddressString = (address) => {
        if (!address) return '-';
        return [
            address.label,
            address.city?.cityname,
            address.state?.statename,
            address.pincode,
            address.country?.countryname
        ].filter(Boolean).join(', ');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Draft': return 'secondary';
            case 'Pending': return 'warning';
            case 'Completed': return 'success';
            case 'Cancelled': return 'danger';
            default: return 'info';
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return <div className="alert alert-danger text-center mt-4">{error}</div>;
    }

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-success mb-0">PURCHASE ENQUIRY - VIEW</h5>
                        <div>
                            {enquiryData && (
                                <Button 
                                    variant="outline-success" 
                                    size="sm" 
                                    className="me-2"
                                    onClick={() => navigate(`/piduplicate/${enquiryData.id}`)}
                                >
                                    <FaCopy className="me-1" />
                                    Duplicate Entry
                                </Button>
                            )}
                            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                                <FaArrowLeft className="me-1" />
                                Back
                            </Button>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Enquiry No</small>
                                <div className="fw-bold">{data.enquiryno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Date</small>
                                <div className="fw-bold">{formatDate(data.enquirydate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Currency</small>
                                <div className="fw-bold">{data.currency || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge bg={getStatusBadge(data.status)} className="px-2 py-1">
                                        {data.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Enquiry Details */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Enquiry Details</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Customer Material Code</small>
                            <div className="fw-bold">{data.customermaterialcode || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Supplier Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Supplier Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Company Name</small>
                            <div className="fw-bold">{data.supplier?.label || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Contact Person</small>
                            <div className="fw-bold">{data.contactperson || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">GST No</small>
                            <div className="fw-bold">{data.supplier?.gstno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Phone</small>
                            <div className="fw-bold">{data.contactno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Email</small>
                            <div className="fw-bold">{data.email || '-'}</div>
                        </Col>
                        <Col md={12}>
                            <small className="text-muted">Address</small>
                            <div className="fw-bold">{getAddressString(data.supplierMultipleAddress)}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Enquiry Items */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Enquiry Items</h6>
                </div>
                <div className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Description</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">Model No</th>
                                    <th className="fw-bold py-2">Tag No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Item Code</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2">Currency</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.purchaseEnquiryItems.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{item.description || '-'}</div>
                                            {item.productremarks && item.productremarks !== '-' && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {item.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{item.partno || '-'}</td>
                                        <td>{item.hsnsacno || '-'}</td>
                                        <td>{item.serialnumber || '-'}</td>
                                        <td>{item.modelnumber || '-'}</td>
                                        <td>{item.tagnumber || '-'}</td>
                                        <td>{item.moc || '-'}</td>
                                        <td>{item.itemcode || '-'}</td>
                                        <td className="text-center">{item.quantity || '0'}</td>
                                        <td className="text-center">{item.uom || '-'}</td>
                                        <td className="fw-semibold">{item.currency || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </Button>
            </div>
        </Container>
    );
}

export default PIView;